import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { CaretRight } from '@liveauctioneers/hammer-ui-core/icons';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { SubtleLink } from '@liveauctioneers/hammer-ui-core/link';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import { getHasSavedSearchItems, savedSearchSearchesSelector } from '@/redux/modules/savedSearch';
import { getHasSavedSearches } from '@/redux/modules/saveSearch';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { toFacetString, toSavedSearchUrl } from '@/utils/savedSearchFacets';
import { getFollowedSearches } from '@/utils/urls';

type Props = {
    className?: string;
};

export const YourFollowedSearches = ({ className }: Props) => {
    const formatMessage = useAppFormatMessage();
    const intl = useIntl();
    const isMobile = useIsMobileLayout();
    const savedSearches = useAppSelector(savedSearchSearchesSelector);
    const hasSavedSearches = useAppSelector(getHasSavedSearches);
    const hasSavedSearchResults = useAppSelector(getHasSavedSearchItems);

    if (!hasSavedSearchResults || !hasSavedSearches) {
        return null;
    }

    return (
        <div className={cn('relative w-full', className)}>
            <div className="flex items-end justify-between">
                <H2>{formatMessage(['yourFollowedSearches', 'header'])}</H2>
                {isMobile && (
                    <div className="flex h-full items-center gap-xs">
                        <SubtleLink
                            className="flex items-center gap-2xs"
                            href={getFollowedSearches()}
                        >
                            {formatMessage(['viewAll'])} <CaretRight size="sm" />
                        </SubtleLink>
                    </div>
                )}
            </div>
            <div className="mt-sm w-full overflow-hidden xsMax:-mx-sm xsMax:w-[calc(100%+32px)]">
                <div
                    className={cn(
                        'scrollbar-hide flex gap-xs overflow-x-auto overflow-y-hidden',
                        'xsMax:snap-x xsMax:snap-mandatory xsMax:scroll-pr-sm xsMax:scroll-pl-sm'
                    )}
                    role="list"
                >
                    {savedSearches.map(({ facets, savedSearchId, searchURL, title }, index) => (
                        <div
                            className={cn('shrink-0 xsMax:snap-start', {
                                'xsMax:pl-sm': index === 0,
                                'xsMax:pr-sm': index === savedSearches.length - 1,
                            })}
                            key={savedSearchId}
                            role="listitem"
                        >
                            <Chip
                                as="a"
                                href={searchURL || toSavedSearchUrl(facets)}
                            >
                                {title || toFacetString(facets, intl)}
                            </Chip>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
